import FocusTrap from "focus-trap-react";
import { CSSProperties, forwardRef } from "react";
import styled from "styled-components";

import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import { REACT_TOASTIFY_ROOT_SELECTOR } from "@/hooks/use-on-click-outside";

type OverlayProps = React.PropsWithChildren<{
  aspectRatio?: string;
  /** Video + stacked content (e.g. description); fills viewport height instead of 16:9 box. */
  stackContent?: boolean;
  /** Short description: shrink-wrap and vertically center like the no-description player. */
  stackContentCompact?: boolean;
  /** Push player chrome below canvas preview header when it is in view (see hook). */
  topOffsetPx?: number;
}>;

export const Overlay = forwardRef<HTMLDivElement, OverlayProps>(
  (props, ref) => {
    const {
      aspectRatio,
      stackContent,
      stackContentCompact = false,
      topOffsetPx = 0,
      children,
    } = props;
    const effectiveTopOffsetPx = stackContent ? topOffsetPx : 0;
    const style: CSSProperties | undefined = stackContent
      ? {
          aspectRatio: "unset",
          overflow: "hidden",
          height: stackContentCompact
            ? "auto"
            : effectiveTopOffsetPx > 0
            ? "100%"
            : "100vh",
          maxHeight: stackContentCompact
            ? "100vh"
            : effectiveTopOffsetPx > 0
            ? "100%"
            : "100vh",
          minHeight: 0,
        }
      : aspectRatio
      ? { aspectRatio }
      : undefined;
    return (
      <OuterContainer $topOffsetPx={effectiveTopOffsetPx}>
        <FocusTrap
          focusTrapOptions={{
            /** Toast portal sits outside the trap; without this, focus-trap blocks mousedown and the toast close click never fires. */
            allowOutsideClick: (event) =>
              event.target instanceof Element &&
              Boolean(event.target.closest(REACT_TOASTIFY_ROOT_SELECTOR)),
          }}
        >
          <InnerContainer ref={ref} style={style}>
            {children}
          </InnerContainer>
        </FocusTrap>
      </OuterContainer>
    );
  }
);

const OuterContainer = styled.div<{ $topOffsetPx: number }>`
  position: fixed;
  z-index: var(--z-index-ultra);
  inset: 0;
  box-sizing: border-box;
  padding-top: ${(props) => props.$topOffsetPx}px;

  background: rgba(30, 34, 45, 0.92);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InnerContainer = withViewportStyle("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 1000px;
  padding: 24px;
  max-height: 100vh;
  aspect-ratio: 16 / 9;

  ${(props) => props.$viewport.tablet`
      padding: 0px;
  `}

  ${(props) => props.$viewport.mobile`
    max-width: unset;
    height: 100vh;
  `}
`;
